import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2, Columns3 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/common";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const Cell = ({ value, onChange, header, testId }) => (
  <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
    className={header ? "w-full bg-transparent px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600 outline-none focus:bg-amber-50" : "w-full bg-transparent px-2 py-1.5 text-sm outline-none focus:bg-amber-50"} />
);

const TableEditor = ({ t, onChange, onRemove }) => {
  const setCol = (i, v) => onChange({ ...t, columns: t.columns.map((c, j) => (j === i ? v : c)) });
  const setCell = (r, c, v) => onChange({ ...t, rows: t.rows.map((row, i) => (i === r ? row.map((x, j) => (j === c ? v : x)) : row)) });
  const addRow = () => onChange({ ...t, rows: [...t.rows, t.columns.map(() => "")] });
  const delRow = (r) => onChange({ ...t, rows: t.rows.filter((_, i) => i !== r) });
  const addCol = () => onChange({ ...t, columns: [...t.columns, "New column"], rows: t.rows.map((r) => [...r, ""]) });
  const delCol = (c) => t.columns.length > 1 && onChange({ ...t, columns: t.columns.filter((_, i) => i !== c), rows: t.rows.map((r) => r.filter((_, i) => i !== c)) });
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={`pricing-table-${t.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
        <input value={t.name} onChange={(e) => onChange({ ...t, name: e.target.value })} className="min-w-[200px] flex-1 bg-transparent font-display text-base font-semibold text-[#0E1E38] outline-none focus:bg-amber-50" data-testid={`pricing-table-name-${t.id}`} />
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={addCol} data-testid={`pricing-add-col-${t.id}`}><Columns3 className="h-3.5 w-3.5" /> Column</Button>
          <Button variant="ghost" size="sm" onClick={addRow} data-testid={`pricing-add-row-${t.id}`}><Plus className="h-3.5 w-3.5" /> Row</Button>
          <Button variant="ghost" size="sm" className="text-rose-700" onClick={onRemove} data-testid={`pricing-remove-table-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr className="border-b border-slate-200 bg-slate-50">
            {t.columns.map((c, i) => <th key={i} className="group relative text-left"><Cell header value={c} onChange={(v) => setCol(i, v)} testId={`pricing-col-${t.id}-${i}`} /><button onClick={() => delCol(i)} title="Remove column" data-testid={`pricing-del-col-${t.id}-${i}`} className="absolute right-1 top-1 text-slate-300 hover:text-rose-700">×</button></th>)}
            <th className="w-8" />
          </tr></thead>
          <tbody>
            {t.rows.map((row, r) => (
              <tr key={r} className="border-b border-slate-100 hover:bg-slate-50/60">
                {row.map((v, c) => <td key={c}><Cell value={v} onChange={(x) => setCell(r, c, x)} testId={`pricing-cell-${t.id}-${r}-${c}`} /></td>)}
                <td><button onClick={() => delRow(r)} className="px-2 text-slate-300 hover:text-rose-700" title="Remove row" data-testid={`pricing-del-row-${t.id}-${r}`}>×</button></td>
              </tr>
            ))}
            {t.rows.length === 0 && <tr><td colSpan={t.columns.length + 1} className="px-3 py-4 text-center text-xs text-slate-400">No rows yet – click “Row”.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const PricingSheet = () => {
  const [sheet, setSheet] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/pricing/sheet").then((r) => setSheet(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);

  const update = (tables) => { setSheet({ ...sheet, tables }); setDirty(true); };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/pricing/sheet", { tables: sheet.tables });
      setSheet(data); setDirty(false); toast.success("Pricing sheet saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!sheet) return <div className="py-10 text-center text-slate-500"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;
  return (
    <Panel title="Sales pricing sheet" id="pricing-sheet" action={
      <div className="flex items-center gap-2">
        {sheet.updated_at && <span className="text-xs text-slate-500">Saved {fmtDate(sheet.updated_at, "d MMM, h:mm a")} by {sheet.updated_by}</span>}
        <Button variant="outline" size="sm" onClick={() => update([...sheet.tables, { id: Math.random().toString(36).slice(2, 10), name: "New price table", columns: ["Item", "Price"], rows: [["", ""]] }])} data-testid="pricing-add-table"><Plus className="h-4 w-4" /> Table</Button>
        <Button size="sm" onClick={save} disabled={!dirty || busy} className="bg-[#0E1E38] text-white hover:bg-[#1a2f55]" data-testid="pricing-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save{dirty ? " changes" : "d"}</Button>
      </div>}>
      <p className="mb-4 text-xs text-slate-500">Click any cell to edit. All prices GST-inclusive. The AI estimator above reads these tables, so keep them current.</p>
      <div className="space-y-5">
        {sheet.tables.map((t) => <TableEditor key={t.id} t={t} onChange={(nt) => update(sheet.tables.map((x) => (x.id === t.id ? nt : x)))} onRemove={() => window.confirm(`Remove table “${t.name}”?`) && update(sheet.tables.filter((x) => x.id !== t.id))} />)}
      </div>
    </Panel>
  );
};
